import { motion } from "framer-motion"
import type { CSSProperties, MouseEvent, ReactNode } from "react"

export interface SharedDownloadImage {
    component: ReactNode
    caption: string
}

export interface SharedDownloadFile {
    src: string
    fileName: string
}

export interface SharedDownloadAction {
    id: string
    label: string
    subLabel?: string
    variant: "solid" | "outline"
    href?: string
    files?: SharedDownloadFile[]
    shareTitle?: string
}

interface SharedDownloadViewProps {
    isAvailable: boolean
    images: SharedDownloadImage[]
    actions: SharedDownloadAction[]
}

const MotionButton = motion.create("button")

const tapHighlightStyle: CSSProperties = {
    WebkitTapHighlightColor: "transparent",
}

const getFile = async ({ src, fileName }: SharedDownloadFile) => {
    const response = await fetch(src)
    const blob = await response.blob()

    return new File([blob], fileName, { type: blob.type || "image/png" })
}

const downloadFile = (file: File) => {
    const objectUrl = URL.createObjectURL(file)
    const link = document.createElement("a")

    link.href = objectUrl
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    link.remove()

    URL.revokeObjectURL(objectUrl)
}

const SharedDownloadView = ({ isAvailable, images, actions }: SharedDownloadViewProps) => {
    const visibleImages = images.filter((image) => image.component)
    const hasContent = visibleImages.length > 0
    const imageGridClass = visibleImages.length === 1 ? "grid-cols-1" : "grid-cols-2"
    const hasSingleAction = actions.length === 1

    const allowContextMenu = (event: MouseEvent<HTMLDivElement>) => {
        if (isAvailable) {
            event.stopPropagation()
        }
    }

    const handleAction = async (action: SharedDownloadAction) => {
        if (!isAvailable) {
            return
        }

        if (action.href) {
            window.open(action.href, "_blank")
            return
        }

        if (!action.files?.length) {
            return
        }

        const files = await Promise.all(action.files.map(getFile))
        const shareNavigator = navigator as Navigator & {
            canShare?: (data: { files: File[] }) => boolean
            share?: (data: { files: File[]; title?: string }) => Promise<void>
        }

        if (shareNavigator.share && shareNavigator.canShare?.({ files })) {
            try {
                await shareNavigator.share({
                    files,
                    title: action.shareTitle,
                })
                return
            } catch (error) {
                if (error instanceof DOMException && error.name === "AbortError") {
                    return
                }
            }
        }

        files.forEach(downloadFile)
    }

    if (!hasContent) {
        return null
    }

    return (
        <div className="relative h-full min-h-0" onContextMenu={allowContextMenu}>
            <div className="flex h-full min-w-0 flex-col gap-4">
                <div className={`grid min-h-0 w-full flex-1 ${imageGridClass} items-stretch justify-center gap-2 sm:gap-4`}>
                    {visibleImages.map((image) => (
                        <figure key={image.caption} className="grid min-h-0 min-w-0 grid-rows-[1fr_auto] gap-2 text-center">
                            <div className="flex min-h-0 min-w-0 items-center justify-center overflow-hidden rounded-2xl border border-black/5">
                                {image.component}
                            </div>
                            <figcaption className="text-sm font-medium text-black/70">
                                {image.caption}
                            </figcaption>
                        </figure>
                    ))}
                </div>

                <div className={`flex shrink-0 items-center gap-2 sm:gap-4 ${hasSingleAction ? "justify-center" : "justify-around"}`}>
                    {actions.map((action) => (
                        <MotionButton
                            key={action.id}
                            type="button"
                            className={
                                action.variant === "solid"
                                    ? `flex h-12 min-w-0 ${hasSingleAction ? "w-1/2" : "flex-1"} select-none flex-col items-center justify-center rounded-full bg-[#3ead2a] px-3 py-1 text-white outline-none focus-visible:ring-2 focus-visible:ring-[#3ead2a]/40 max-w-[176px]`
                                    : `h-12 min-w-0 ${hasSingleAction ? "w-1/2" : "flex-1"} select-none rounded-full border border-[#3ead2a] bg-white px-3 py-2 text-sm font-semibold text-[#3ead2a] outline-none focus-visible:ring-2 focus-visible:ring-[#3ead2a]/40 sm:text-base md:text-xl max-w-[176px]`
                            }
                            style={tapHighlightStyle}
                            onClick={() => handleAction(action)}
                            whileHover={{ y: -2, scale: 1.04 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ duration: 0.18, ease: "easeOut" }}
                        >
                            <span className="whitespace-nowrap text-sm font-semibold leading-tight sm:text-base">
                                {action.label}
                            </span>
                            {action.subLabel && (
                                <span className="whitespace-nowrap text-[12px] font-medium leading-tight sm:text-[13px]">
                                    {action.subLabel}
                                </span>
                            )}
                        </MotionButton>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default SharedDownloadView
